import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../services/HttpManager";
import { cuando, errorTexto, nombreCompleto } from "./salesHelpers";
import { telefonoBonito } from "../../../utils/telefonoBonito";

interface Props {
	campusId: number;
	/** "ventas" (CRM) o el departamento de la bandeja: caja, kits, academico, rrhh. */
	dept: string;
	deptNombre?: string;
	/** Bandeja "Todas" con varias áreas: se elige a cuál entra. */
	opciones?: { dept: string; nombre: string }[];
	onClose: () => void;
	/** Abrir la conversación de esa persona (ya quedó en la bandeja si es de un departamento). */
	onAbrir: (prospectId: number) => void;
	/** Ventas: el número no existe → el alta completa con el teléfono ya puesto. */
	onAlta?: (telefono: string) => void;
}

const LADAS: { k: string; t: string }[] = [
	{ k: "52", t: "MX +52" }, { k: "504", t: "HN +504" }, { k: "1", t: "EE. UU. +1" }, { k: "503", t: "SV +503" }, { k: "502", t: "GT +502" },
];
const PAIS_DE_PLANTEL: { [id: number]: string } = { 1: "52", 2: "52", 3: "504", 4: "504", 7: "503" };
/** Dígitos del número nacional por lada: un número de Honduras con +52 no es de nadie y crearía una ficha basura. */
const LARGO: { [lada: string]: number } = { "52": 10, "504": 8, "503": 8, "502": 8, "1": 10 };

/**
 * Nuevo Chat (Esteban, 2026-10-17): escribirle a un número aunque no esté en mi bandeja. Primero se busca en el CRM (teléfono
 * principal o segundo); en Caja, Kits y Académico la persona queda en la bandeja del departamento con toda su conversación,
 * y si tiene la ventana de 24 h abierta en algún número, se le contesta desde ese (libre, sin costo). Si nunca escribió,
 * igual se le manda plantilla. En Ventas un número nuevo pasa al alta de siempre.
 */
const NuevoChat: React.FC<Props> = ({ campusId, dept: deptIn, deptNombre: nombreIn, opciones, onClose, onAbrir, onAlta }) => {
	const http = HttpManager.getInstance();
	const [dept, setDept] = useState<string>(opciones && opciones.length && !opciones.some((o) => o.dept === deptIn) ? opciones[0].dept : deptIn);
	const deptNombre = opciones?.find((o) => o.dept === dept)?.nombre || nombreIn;
	const esBandeja = dept !== "ventas";
	const [lada, setLada] = useState<string>(PAIS_DE_PLANTEL[campusId] || "504");
	const [num, setNum] = useState<string>("");
	const [nombre, setNombre] = useState<string>("");
	const [lookup, setLookup] = useState<{ valid: boolean; phone: string | null; match: any } | null>(null);
	const [buscando, setBuscando] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const timer = useRef<any>(null);
	const turno = useRef<number>(0);
	const ocupado = useRef<boolean>(false);
	/** El teléfono que contestó la última búsqueda: si ya cambió, no se abre nada con el resultado viejo. */
	const [buscado, setBuscado] = useState<string>("");

	// Con "+" se respeta lo escrito; si no, la lada elegida + los dígitos (como el alta de prospectos).
	const telefono = useMemo(() => {
		const d = num.replace(/\D/g, "");
		if (!d) return "";
		if (num.trim().startsWith("+")) return "+" + d;
		return "+" + lada + (d.startsWith(lada) && d.length > lada.length + 7 ? d.slice(lada.length) : d);
	}, [num, lada]);

	// Con "+" se respeta lo escrito; con lada, el número nacional debe tener los dígitos de ese país.
	const largoMal = !num.trim().startsWith("+") && !!LARGO[lada] && num.replace(/\D/g, "").length >= 8 && num.replace(/\D/g, "").replace(new RegExp("^" + lada), "").length !== LARGO[lada];

	useEffect(() => {
		clearTimeout(timer.current);
		setError(null);
		setLookup(null);
		const yo = ++turno.current;
		if (num.replace(/\D/g, "").length < 8 || largoMal) { setBuscando(false); return; }
		setBuscando(true);
		timer.current = setTimeout(async () => {
			try {
				const r = (await http.prospectsService.lookupPhone(campusId, telefono)).data;
				if (yo === turno.current) { setLookup(r); setBuscado(telefono); }
			} catch (e) { if (yo === turno.current) setLookup(null); }
			finally { if (yo === turno.current) setBuscando(false); }
		}, 350);
		return () => clearTimeout(timer.current);
	}, [telefono, largoMal]);

	const m = lookup?.match;
	const valido = !!lookup?.valid && buscado === telefono && !largoMal;

	const abrir = async () => {
		if (!valido || buscando || ocupado.current) return;
		if (!esBandeja) {
			if (m) onAbrir(m.id);
			else if (onAlta) onAlta(lookup?.phone || telefono);
			return;
		}
		ocupado.current = true;
		setBusy(true); setError(null);
		try {
			const r = await http.prospectsService.nuevoChat(campusId, dept, lookup?.phone || telefono, m ? null : nombre.trim() || null);
			onAbrir(r.data.prospect_id);
		} catch (e) { setError(errorTexto(e)); }
		finally { ocupado.current = false; setBusy(false); }
	};

	const boton = !esBandeja && !m ? "Dar de Alta" : "Abrir Chat";
	return (
		<Modal show onHide={onClose} className="nchat-modal" aria-labelledby="nchat-titulo" centered>
			<Modal.Header closeButton closeLabel="Cerrar">
				<Modal.Title id="nchat-titulo">Nuevo Chat{deptNombre ? <span className="nchat-dept">{deptNombre}</span> : null}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{opciones && opciones.length > 1 ? (
					<>
						<label htmlFor="nchat-area" className="nchat-l">Área</label>
						<select id="nchat-area" value={dept} onChange={(e) => setDept(e.target.value)}>
							{opciones.map((o) => <option key={o.dept} value={o.dept}>{o.nombre}</option>)}
						</select>
					</>
				) : null}
				<label htmlFor="nchat-num" className="nchat-l">Teléfono</label>
				<div className="nchat-tel">
					<select aria-label="Lada" value={lada} onChange={(e) => setLada(e.target.value)}>
						{LADAS.map((l) => <option key={l.k} value={l.k}>{l.t}</option>)}
					</select>
					<input id="nchat-num" type="tel" inputMode="tel" autoFocus autoComplete="off" value={num} onChange={(e) => setNum(e.target.value)}
						onKeyDown={(e) => { if (e.key === "Enter") abrir(); }} placeholder={lada === "52" ? "81 1234 5678" : lada === "504" ? "9999 9999" : "Número"} />
				</div>
				{largoMal ? <div className="nchat-estado oro">Con {LADAS.find((l) => l.k === lada)?.t} el número lleva {LARGO[lada]} dígitos: revisa la lada.</div>
					: buscando ? <div className="nchat-estado">Buscando…</div>
					: lookup && !valido ? <div className="nchat-estado oro">Ese número no parece válido.</div> : null}
				{m ? (
					<div className="nchat-res existe">
						<b>{nombreCompleto(m) || telefonoBonito(lookup?.phone)}</b>
						<span>{[m.etapa, m.status, m.owner_user_name || m.owner_zoho_name || "Sin asesora", m.first_contact_at ? "desde " + cuando(m.first_contact_at) : null].filter(Boolean).join(" · ")}</span>
						{m.phone_e164 && lookup?.phone && m.phone_e164 !== lookup.phone
							? <span className="nchat-nota">Es su segundo teléfono: los mensajes van a su número principal, {telefonoBonito(m.phone_e164)}.</span> : null}
						{esBandeja ? <span className="nchat-nota">Su conversación completa pasa a la bandeja de {deptNombre || "tu área"}.</span> : null}
					</div>
				) : valido && !buscando ? (
					<div className="nchat-res nuevo">
						<b>{telefonoBonito(lookup?.phone)} · no está en el CRM</b>
						{esBandeja ? (
							<>
								<label htmlFor="nchat-nom" className="nchat-l">Nombre (opcional)</label>
								<input id="nchat-nom" value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="off" placeholder="Si es alumno, sale de su ficha" />
							</>
						) : <span>Se da de alta como prospecto.</span>}
					</div>
				) : null}
				{error ? <div className="nchat-estado oro" role="alert">{error}</div> : null}
			</Modal.Body>
			<Modal.Footer>
				<button type="button" className="nchat-b" onClick={onClose}>Cancelar</button>
				<button type="button" className="nchat-b naranja" disabled={!valido || buscando || busy} onClick={abrir}>{busy ? "Abriendo…" : boton}</button>
			</Modal.Footer>
		</Modal>
	);
};

export default NuevoChat;

/** El ícono de la barra de la lista (globo con +), igual en computadora y celular. */
export const BotonNuevoChat: React.FC<{ onClick: () => void; enVentas?: boolean }> = ({ onClick, enVentas }) => (
	<button className={"wl-icono wl-nuevo" + (enVentas ? " en-ventas" : "")} onClick={onClick} aria-label="Nuevo Chat" title="Nuevo Chat">
		<svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
			<path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H10l-4.2 3.6c-.5.4-1.3.1-1.3-.6V16.3A2.5 2.5 0 0 1 4 14V5.5Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
			<path d="M12 6.5v6M9 9.5h6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
		</svg>
	</button>
);
